/* Hypoteční dotazník ve složce klienta — strana poradce.
 *
 * CO TO DĚLÁ: vyrobí odkaz na dotazník, složí k němu zprávu a dá ji do
 * schránky. Ukáže stav (neodesláno / čeká / rozpracováno X % / vyplněno),
 * nechá odkaz zneplatnit, vyplněný dotazník přečíst po sekcích a vytisknout
 * do PDF pro banku.
 *
 * CO TO NEDĚLÁ A DĚLAT NEBUDE: neodesílá. Žádný e-mail, žádná SMS, žádný
 * WhatsApp přes server. Odkaz vede na formulář, který se ptá na rodné číslo —
 * kdyby ho rozesílala aplikace, jedna špatně uložená adresa u klienta by
 * poslala výzvu k vyplnění rodného čísla cizímu člověku, a to jménem poradce.
 * Zpráva se proto jen připraví a poradce ji odešle svým kanálem, kde vidí,
 * komu ji posílá. Tlačítko WhatsApp je jen odkaz wa.me — otevře poradci jeho
 * vlastní aplikaci, nic neodesílá na pozadí.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Copy, FileText, Link2, MessageCircle, Printer, ShieldAlert, Ban, RefreshCw } from "lucide-react";
import { osloveniProZpravu } from "@/lib/czechNames";
import { zpravaDotaznik, datumCesky } from "@/lib/crm/zpravaDotaznik";
import { whatsappOdkaz } from "@/lib/crm/zpravaDoporuceni";
import { STAV, STAV_POPIS, odvodStav, zbyvaDni, nejnovejsi, jeVyplneno } from "./stavDotazniku";
import { nactiDotazniky, vytvorOdkaz, zneplatniOdkaz, nactiDetail, odhalPole, nactiPlny } from "./dotaznikApi";
import DotaznikCteni from "./DotaznikCteni";
import { otevriTiskDotazniku } from "./tiskDotazniku";

/* Nabídka platnosti odkazu. Čtrnáct dní je výchozí: klient si musí najít
   výplatní pásku, občanku a údaje o nemovitosti, což přes víkend nestihne —
   a zároveň to není odkaz, který visí na internetu půl roku. */
const PLATNOSTI = [7, 14, 30];
const PLATNOST_VYCHOZI = 14;

/** Zkopíruje do schránky. Vrací false, když prohlížeč schránku nedovolí. */
async function zkopiruj(text) {
  try {
    if (!navigator?.clipboard?.writeText) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch (e) {
    return false;
  }
}

function StavOdznak({ stav, lang }) {
  const popis = STAV_POPIS[stav.klic] || STAV_POPIS[STAV.NEODESLANO];
  const label = lang === "sk" ? popis.sk : popis.cs;
  /* Procenta se píšou JEN u rozpracovaného a JEN když je opravdu známe.
     Bez počtů polí zůstane odznak beze změny — vymyšlené číslo by poradce
     bral jako fakt o tom, jak daleko klient je. */
  const pct = stav.klic === STAV.ROZPRACOVANO && stav.procenta != null ? ` · ${stav.procenta} %` : "";
  return (
    <span
      data-testid="hypoteka-stav-badge"
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-drobny font-bold"
      style={{ background: "var(--surface-2)", color: popis.barva, border: `1px solid ${popis.barva}` }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: popis.barva, display: "inline-block" }} />
      {label}{pct}
    </span>
  );
}

export default function HypotekaDotaznikKarta({ contact, advisor = null, lang = "cs" }) {
  const contactId = contact?.id;
  const [polozky, setPolozky] = useState(null);   // null = ještě nenačteno
  const [chyba, setChyba] = useState("");
  const [tvorim, setTvorim] = useState(false);
  const [zprava, setZprava] = useState("");
  const [platnost, setPlatnost] = useState(PLATNOST_VYCHOZI);
  const [detail, setDetail] = useState(null);
  const [detailChyba, setDetailChyba] = useState("");
  const [potvrzeniPdf, setPotvrzeniPdf] = useState(false);
  const [tiskne, setTiskne] = useState(false);

  const L = (sk, cs) => (lang === "sk" ? sk : cs);

  const nacti = useCallback(async () => {
    if (!contactId) return;
    setChyba("");
    try {
      const data = await nactiDotazniky(contactId);
      setPolozky(data);
    } catch (e) {
      /* Chyba načtení NENÍ prázdný stav. Kdyby se ukázalo „neodesláno“,
         poradce by poslal druhý odkaz na dotazník, který klient možná
         vyplňuje — a vyplňoval by ho podruhé od začátku. */
      setPolozky(null);
      setChyba(L("Stav dotazníka sa nepodarilo načítať.", "Stav dotazníku se nepodařilo načíst."));
    }
  }, [contactId, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { nacti(); }, [nacti]);

  /* Nejnovější dotazník rozhoduje o stavu karty. Starší (vypršelé) zůstávají
     v seznamu níž, aby bylo vidět, že se už jednou posílal.
     ŘADÍ SE TU ZNOVU, i když je řazení součástí kontraktu se serverem.
     Spoléhat na pořadí z databáze je past: při [starý vypršelý, nový
     rozpracovaný] by odznak hlásil „Odkaz vypršel“, poradce by vyrobil druhý
     odkaz a klient, který zrovna vyplňuje ten první, by začínal od začátku.
     Dvě řádky navíc proti hodině klientovy práce. */
  const aktualni = useMemo(() => nejnovejsi(polozky), [polozky]);
  const stav = odvodStav(aktualni);

  /* Detail se dotahuje jen tehdy, když je co číst. U čekajícího odkazu není
     důvod tahat na obrazovku odpovědi, které ještě neexistují. */
  useEffect(() => {
    let zruseno = false;
    const maObsah = stav.klic === STAV.VYPLNENO || stav.klic === STAV.ROZPRACOVANO;
    if (!aktualni?.id || !maObsah) { setDetail(null); setDetailChyba(""); return undefined; }
    setDetailChyba("");
    nactiDetail(aktualni.id)
      .then((d) => { if (!zruseno) setDetail(d); })
      .catch(() => { if (!zruseno) { setDetail(null); setDetailChyba(L("Odpovede sa nepodarilo načítať.", "Odpovědi se nepodařilo načíst.")); } });
    return () => { zruseno = true; };
  }, [aktualni?.id, stav.klic]); // eslint-disable-line react-hooks/exhaustive-deps

  const slozZpravu = (urlPath, platiDo) => {
    /* Odkaz skládáme z cesty, kterou vrátil SERVER — v ní je token. Frontend
       si adresu nikdy nesestaví z id klienta; kdyby ano, stačilo by uhodnout
       id a dotazník cizího klienta by se otevřel. */
    const cesta = String(urlPath || "").trim();
    if (!cesta) return "";
    const plnaUrl = `${window.location.origin}${cesta}`;
    /* Oslovení řeší celé czechNames: vybere správný token (u vietnamského jména
       Nguyen Duc Trung je oslovovací jméno POSLEDNÍ, ne první — dosavadní
       split podle mezery vracel příjmení), skloní do 5. pádu, pro SK nominativ.
       Když si tvarem není jistý, vrátí prázdno → zpráva začne jen pozdravem. */
    const osloveni = osloveniProZpravu(contact?.name, lang);
    return zpravaDotaznik({
      osloveni,
      odkaz: plnaUrl,
      platiDo,
      poradce: advisor?.name || "",
      lang,
    });
  };

  const vytvor = async () => {
    if (!contactId || tvorim) return;
    setTvorim(true);
    try {
      const data = await vytvorOdkaz(contactId, platnost);
      const text = slozZpravu(data?.url_path, data?.plati_do);
      if (!text) {
        /* Server nevrátil cestu → není co poslat. Radši hlášku než odkaz
           poskládaný naslepo, který by nikam nevedl. */
        toast.error(L("Odkaz sa nevytvoril — server nevrátil adresu.", "Odkaz se nevytvořil — server nevrátil adresu."));
        return;
      }
      setZprava(text);
      const ok = await zkopiruj(text);
      toast.success(
        ok ? L("Správa skopírovaná", "Zpráva zkopírována") : L("Odkaz vytvorený", "Odkaz vytvořen"),
        { description: ok
          ? L("Vlož ju klientovi do WhatsAppu alebo e-mailu. Appka nič neposiela.", "Vlož ji klientovi do WhatsAppu nebo e-mailu. Aplikace nic neposílá.")
          : L("Schránka nie je dostupná — text skopíruj z poľa nižšie.", "Schránka není dostupná — text zkopíruj z pole níž.") },
      );
      await nacti();
    } catch (e) {
      toast.error(e?.response?.data?.detail || L("Odkaz sa nepodarilo vytvoriť", "Odkaz se nepodařilo vytvořit"));
    } finally {
      setTvorim(false);
    }
  };

  const znovuZprava = () => {
    const text = slozZpravu(aktualni?.url_path, aktualni?.plati_do);
    if (!text) {
      toast.error(L("Odkaz už nie je k dispozícii — vytvor nový.", "Odkaz už není k dispozici — vytvoř nový."));
      return;
    }
    setZprava(text);
    zkopiruj(text).then((ok) => {
      if (ok) toast.success(L("Skopírované", "Zkopírováno"));
    });
  };

  const zneplatni = async () => {
    if (!aktualni?.id) return;
    try {
      await zneplatniOdkaz(aktualni.id);
      setZprava("");
      toast.success(L("Odkaz zneplatnený — formulár sa už neotvorí.", "Odkaz zneplatněn — formulář se už neotevře."));
      await nacti();
    } catch (e) {
      toast.error(L("Zneplatnenie zlyhalo", "Zneplatnění se nepovedlo"));
    }
  };

  const jmenoKlienta = `${contact?.name || ""} ${contact?.surname || ""}`.trim();

  const tiskInterni = () => {
    if (!detail) return;
    try {
      otevriTiskDotazniku({
        sekce: detail.sekce, odpovedi: detail.odpovedi, klient: jmenoKlienta,
        advisor, plne: false, lang,
      });
    } catch (e) {
      toast.error(L("Prehliadač zablokoval okno na tlač.", "Prohlížeč zablokoval okno pro tisk."));
    }
  };

  const tiskProBanku = async () => {
    if (!aktualni?.id || tiskne) return;
    setTiskne(true);
    try {
      /* Plné hodnoty se stahují AŽ TEĎ, po potvrzení. Kdyby se tahaly spolu
         s detailem, měl by prohlížeč rodná čísla v paměti pokaždé, když si
         poradce jen otevřel záložku. */
      const plny = await nactiPlny(aktualni.id);

      /* ODPOVĚĎ SE MUSÍ ZKONTROLOVAT, ať se netiskne prázdný papír.
         Bez téhle kontroly stačilo, aby server odpověděl 200 s prázdným tělem
         (výpadek, nasazená starší routa) — `plny?.odpovedi` spadlo na default
         {}, `plny?.sekce || detail?.sekce` na definici z detailu, okno se
         otevřelo a v dokumentu byla hlavička, červený pruh o rodném čísle,
         názvy všech polí a u každého pomlčka. Poradce by to poslal bance jako
         vyplněný dotazník, ve kterém klient prý neodpověděl na nic. */
      const sekce = Array.isArray(plny?.sekce) ? plny.sekce : null;
      const maOdpovedi = plny?.odpovedi && typeof plny.odpovedi === "object"
        && Object.values(plny.odpovedi).some(jeVyplneno);
      if (!sekce || !maOdpovedi) {
        toast.error(
          L("Plné údaje sa nenačítali — dokument sme neotvorili.", "Plné údaje se nenačetly — dokument jsme neotevřeli."),
          { description: L(
            "Neznamená to, že klient nič nevyplnil. Skús to prosím znova.",
            "Neznamená to, že klient nic nevyplnil. Zkus to prosím znovu.",
          ) },
        );
        return;
      }

      otevriTiskDotazniku({
        sekce, odpovedi: plny.odpovedi, klient: jmenoKlienta,
        advisor, plne: true, lang,
      });
      setPotvrzeniPdf(false);
    } catch (e) {
      toast.error(e?.message === "popup-blocked"
        ? L("Prehliadač zablokoval okno na tlač.", "Prohlížeč zablokoval okno pro tisk.")
        : L("Plné údaje sa nepodarilo načítať.", "Plné údaje se nepodařilo načíst."));
    } finally {
      setTiskne(false);
    }
  };

  const dniDoKonce = zbyvaDni(stav.plati_do);
  const platiDoText = datumCesky(stav.plati_do, lang);
  const odkazZivy = stav.klic === STAV.CEKA || stav.klic === STAV.ROZPRACOVANO;

  return (
    <div className="space-y-3" data-testid="hypoteka-dotaznik-karta">
      {/* ── hlavička se stavem ────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <FileText size={14} style={{ color: "var(--accent)" }} />
        <span className="text-[12px] font-bold" style={{ color: "var(--ink)" }}>
          {L("Dotazník k hypotéke", "Dotazník k hypotéce")}
        </span>
        {chyba ? null : <StavOdznak stav={stav} lang={lang} />}
        {odkazZivy && platiDoText && (
          <span className="text-drobny" style={{ color: "var(--muted)" }} data-testid="hypoteka-platnost">
            {dniDoKonce != null && dniDoKonce <= 3
              ? L(`odkaz platí do ${platiDoText} (posledné dni)`, `odkaz platí do ${platiDoText} (poslední dny)`)
              : L(`odkaz platí do ${platiDoText}`, `odkaz platí do ${platiDoText}`)}
          </span>
        )}
      </div>

      {chyba && (
        <div className="rounded-lg px-3 py-2.5" data-testid="hypoteka-chyba"
          style={{ background: "rgba(197,48,48,0.08)", border: "1px solid rgba(197,48,48,0.25)" }}>
          <div className="text-drobny font-semibold" style={{ color: "var(--negative-text)" }}>{chyba}</div>
          <div className="text-drobny mt-0.5" style={{ color: "var(--muted)" }}>
            {L("Neznamená to, že dotazník neexistuje — dáta sa nenačítali.", "Neznamená to, že dotazník neexistuje — data se nenačetla.")}
          </div>
          <button onClick={nacti} data-testid="hypoteka-retry"
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-drobny font-semibold"
            style={{ background: "var(--ink, #26221A)", color: "var(--bg, #F7F4EC)" }}>
            <RefreshCw size={11} /> {L("Skúsiť znova", "Zkusit znovu")}
          </button>
        </div>
      )}

      {/* ── vytvoření odkazu ─────────────────────────────────────────── */}
      {!chyba && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={vytvor}
            disabled={tvorim || !contactId}
            data-testid="hypoteka-vytvorit"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-semibold"
            style={{ background: "var(--accent-soft)", color: "var(--accent)", border: "1px solid var(--accent)" }}
          >
            <Link2 size={13} />
            {tvorim
              ? L("Tvorím…", "Tvořím…")
              : (aktualni ? L("Poslať nový dotazník", "Poslat nový dotazník") : L("Poslať dotazník k hypotéke", "Poslat dotazník k hypotéce"))}
          </button>

          <label className="text-drobny flex items-center gap-1" style={{ color: "var(--muted)" }}>
            {L("platnosť", "platnost")}
            <select
              value={platnost}
              onChange={(e) => setPlatnost(Number(e.target.value))}
              data-testid="hypoteka-platnost-vyber"
              className="rounded-md px-1.5 py-1 text-drobny"
              style={{ background: "var(--surface-2)", border: "1px solid var(--line)", color: "var(--ink)" }}
            >
              {PLATNOSTI.map((d) => <option key={d} value={d}>{d} {L("dní", "dní")}</option>)}
            </select>
          </label>

          {odkazZivy && (
            <>
              <button onClick={znovuZprava} data-testid="hypoteka-znovu-zprava"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-drobny font-semibold"
                style={{ background: "var(--surface-2)", color: "var(--muted)", border: "1px solid var(--line)" }}>
                <Copy size={11} /> {L("Skopírovať správu znova", "Zkopírovat zprávu znovu")}
              </button>
              <button onClick={zneplatni} data-testid="hypoteka-zneplatnit"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-drobny font-semibold"
                style={{ background: "rgba(197,48,48,0.08)", color: "var(--negative-text)", border: "1px solid rgba(197,48,48,0.35)" }}>
                <Ban size={11} /> {L("Zneplatniť odkaz", "Zneplatnit odkaz")}
              </button>
            </>
          )}
        </div>
      )}

      {/* ── hotová zpráva k odeslání ─────────────────────────────────── */}
      {zprava && (
        <div className="flex flex-col gap-2">
          <textarea
            value={zprava}
            onChange={(e) => setZprava(e.target.value)}
            rows={11}
            data-testid="hypoteka-zprava"
            className="w-full rounded-lg p-2.5 text-drobny leading-relaxed resize-y"
            style={{ background: "var(--surface-2)", border: "1px solid var(--line)", color: "var(--ink)" }}
          />
          <div className="flex gap-1.5 flex-wrap">
            <a
              href={whatsappOdkaz(contact?.phone, zprava)}
              target="_blank"
              rel="noreferrer"
              data-testid="hypoteka-whatsapp"
              className="flex-1 min-w-[160px] flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[12px] font-semibold"
              style={{ background: "var(--positive-soft)", color: "var(--positive-text)", border: "1px solid var(--positive)" }}
            >
              <MessageCircle size={13} /> {L("Otvoriť WhatsApp", "Otevřít WhatsApp")}
            </a>
            <button
              onClick={async () => {
                const ok = await zkopiruj(zprava);
                toast[ok ? "success" : "error"](ok ? L("Skopírované", "Zkopírováno") : L("Schránka nie je dostupná", "Schránka není dostupná"));
              }}
              data-testid="hypoteka-kopirovat"
              className="px-3 py-2 rounded-full text-[12px] font-semibold"
              style={{ background: "var(--surface-2)", color: "var(--muted)", border: "1px solid var(--line)" }}
            >
              {L("Kopírovať", "Kopírovat")}
            </button>
          </div>
          <p className="text-drobny" style={{ color: "var(--muted)" }}>
            {L(
              "Text si uprav, ako potrebuješ. Aplikácia nič neodosiela — správu pošleš ty.",
              "Text si uprav, jak potřebuješ. Aplikace nic neodesílá — zprávu pošleš ty.",
            )}
          </p>
        </div>
      )}

      {/* ── vyplněný / rozpracovaný obsah ────────────────────────────── */}
      {detailChyba && (
        <div className="text-drobny" data-testid="hypoteka-detail-chyba" style={{ color: "var(--negative-text)" }}>{detailChyba}</div>
      )}

      {detail && (
        <div className="pt-1 space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            <button onClick={() => setPotvrzeniPdf(true)} data-testid="hypoteka-pdf-banka"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-drobny font-semibold"
              style={{ background: "var(--ink, #26221A)", color: "var(--bg, #F7F4EC)" }}>
              <Printer size={11} /> {L("PDF pre banku", "PDF pro banku")}
            </button>
            <button onClick={tiskInterni} data-testid="hypoteka-pdf-interni"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-drobny font-semibold"
              style={{ background: "var(--surface-2)", color: "var(--muted)", border: "1px solid var(--line)" }}>
              <Printer size={11} /> {L("PDF bez citlivých údajov", "PDF bez citlivých údajů")}
            </button>
          </div>

          {potvrzeniPdf && (
            <div className="rounded-lg px-3 py-2.5" data-testid="hypoteka-pdf-potvrzeni"
              style={{ background: "rgba(197,48,48,0.07)", border: "1px solid rgba(197,48,48,0.3)" }}>
              <div className="flex items-start gap-2">
                <ShieldAlert size={14} style={{ color: "var(--negative-text)", flexShrink: 0, marginTop: 2 }} />
                <div className="text-drobny" style={{ color: "var(--ink)" }}>
                  {L(
                    "Dokument bude obsahovať rodné číslo a čísla dokladov v plnom znení. Otvorí sa v novom okne — pozor, čo máš na obrazovke.",
                    "Dokument bude obsahovat rodné číslo a čísla dokladů v plném znění. Otevře se v novém okně — pozor, co máš na obrazovce.",
                  )}
                </div>
              </div>
              <div className="flex gap-1.5 mt-2">
                <button onClick={tiskProBanku} disabled={tiskne} data-testid="hypoteka-pdf-potvrdit"
                  className="px-3 py-1.5 rounded-full text-drobny font-semibold"
                  style={{ background: "#C53030", color: "#fff" }}>
                  {tiskne ? L("Pripravujem…", "Připravuji…") : L("Áno, vytlačiť pre banku", "Ano, vytisknout pro banku")}
                </button>
                <button onClick={() => setPotvrzeniPdf(false)} data-testid="hypoteka-pdf-zrusit"
                  className="px-3 py-1.5 rounded-full text-drobny font-semibold"
                  style={{ background: "var(--surface-2)", color: "var(--muted)", border: "1px solid var(--line)" }}>
                  {L("Zrušiť", "Zrušit")}
                </button>
              </div>
            </div>
          )}

          <DotaznikCteni
            sekce={detail.sekce}
            odpovedi={detail.odpovedi}
            lang={lang}
            onOdhalit={async (poleId) => {
              const r = await odhalPole(aktualni.id, poleId);
              return r?.hodnota;
            }}
          />
        </div>
      )}

      {/* ── prázdný stav ─────────────────────────────────────────────── */}
      {!chyba && Array.isArray(polozky) && polozky.length === 0 && !zprava && (
        <p className="text-drobny" data-testid="hypoteka-prazdno" style={{ color: "var(--muted)" }}>
          {L(
            "Klientovi si dotazník ešte neposielal. Odkaz vyrobíš tlačidlom vyššie — správu si potom skopíruješ a pošleš sám.",
            "Klientovi jsi dotazník ještě neposílal. Odkaz vyrobíš tlačítkem výš — zprávu si pak zkopíruješ a pošleš sám.",
          )}
        </p>
      )}
    </div>
  );
}
